import i18n from "../../i18n";
import React, { useState, useEffect } from "react";
import {
  Stack,
  TextInput,
  Text,
  Select,
  MultiSelect,
  SimpleGrid,
  Paper,
  Tabs,
  Box,
} from "@mantine/core";
import RemoteSelect2 from "../remoteSelect2";
import McaNotificationTemplateInput from "./McaNotificationTemplateInput";
import McaNotificationConditionsEditor from "./McaNotificationConditionsEditor";
import EntityDocumentEmailFields from "../entityDocumentEmailFields";
import {
  defaultStepNotification,
  entityRoleFromNotification,
  entityRoleOptions,
  formatStaffLabel,
  getMcaNotificationTemplateVars,
  MCA_NOTIFY_BY,
  MCA_NOTIFY_CHANNELS,
  notificationRecipientMode,
  APPROVED_SEND_TIMING_NOTE,
  sendTimingFlags,
  sendTimingFromNotification,
  sendTimingSelectData,
} from "../../data/mcaEntities";
import { useMcaEditorReadOnly } from "./mcaEditorReadOnly";
import { useProviderEmailPurposes } from "../../hooks/useProviderEmailPurposes";
import { useServerApi } from "../../hooks/userServerApi";

const FieldCard = ({ title, children }) => (
  <Paper withBorder p="md" radius="sm">
    <Text size="xs" weight={600} color="dimmed" mb="sm" transform="uppercase">
      {title}
    </Text>
    {children}
  </Paper>
);

export const notificationSectionForError = (error) => {
  const message = String(error || "");
  if (message.startsWith("Email")) return "email";
  if (message.toLowerCase().includes("recipient")) return "recipients";
  return "setup";
};

const clearedEntityRole = {
  notifyMaker: false,
  notifyEntityUser: false,
  notifyEntityClient: false,
  notifyEntityConsultants: false,
  notifyEntityProvider: false,
  notifyEntityProviderPurpose: "",
};

const McaStepNotificationForm = ({
  value,
  onChange,
  targetEntity,
  stepFields = [],
  section = "setup",
  onSectionChange,
}) => {
  const readOnly = useMcaEditorReadOnly();
  const [uncontrolledSection, setUncontrolledSection] = useState(section);
  const sectionValue = onSectionChange ? section : uncontrolledSection;
  const setSection = (next) => {
    if (onSectionChange) onSectionChange(next);
    else setUncontrolledSection(next);
  };
  const notification = { ...defaultStepNotification(), ...value };
  const templateVariables = getMcaNotificationTemplateVars(targetEntity);
  const [emailOptions, setEmailOptions] = useState(() => notification.notifyEmails ?? []);
  const recipientMode = notificationRecipientMode(notification);
  const entityRole = entityRoleFromNotification(notification);
  const { options: insurerPurposeOptions } = useProviderEmailPurposes(
    notification.notifyEntityProviderPurpose
  );
  const [api] = useServerApi();
  const [smsEnabled, setSmsEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.FwdNotification?.getEnabled?.()
      .then((result) => {
        if (!cancelled) setSmsEnabled(!!result?.data?.sms);
      })
      .catch(() => {
        if (!cancelled) setSmsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => {
    setEmailOptions((prev) => {
      const merged = [...prev];
      (notification.notifyEmails ?? []).forEach((e) => {
        if (e && !merged.includes(e)) merged.push(e);
      });
      return merged;
    });
  }, [notification.notifyEmails]);

  const patch = (p) => {
    if (readOnly) return;
    onChange({ ...notification, ...p });
  };

  const handleNotifyByChange = (notifyBy) => {
    if (!notifyBy) return;
    patch({
      notifyBy,
      notifyEmails: notifyBy === "EMAILS" ? notification.notifyEmails : [],
      notifyUsers: notifyBy === "USERS" ? notification.notifyUsers : [],
      notifyRoles: notifyBy === "USER_ROLE" ? notification.notifyRoles : [],
      notifyNextApprovers: notifyBy === "NEXT_APPROVERS",
      ...(notifyBy === "ENTITY_ROLE" ? {} : clearedEntityRole),
    });
  };

  const handleEntityRoleChange = (role) => {
    patch({
      notifyBy: "ENTITY_ROLE",
      notifyEmails: [],
      notifyUsers: [],
      notifyRoles: [],
      notifyNextApprovers: false,
      notifyMaker: role === "maker",
      notifyEntityUser: role === "user",
      notifyEntityClient: role === "client",
      notifyEntityConsultants: role === "consultants",
      notifyEntityProvider: role === "insurer",
      notifyEntityProviderPurpose:
        role === "insurer" ? notification.notifyEntityProviderPurpose : "",
    });
  };

  const handleSendTimingChange = (timing) => {
    if (!timing || timing.includes(",")) return;
    patch(sendTimingFlags(timing));
  };

  const isEmail = (notification.channel ?? "EMAIL") === "EMAIL";
  const channelOptions = MCA_NOTIFY_CHANNELS.filter(
    (option) => option.value !== "SMS" || smsEnabled || notification.channel === "SMS"
  );
  const activeSection = !isEmail && sectionValue === "email" ? "setup" : sectionValue;

  useEffect(() => {
    if (!isEmail && sectionValue === "email") setSection("setup");
    // setSection identity follows the latest onSectionChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmail, sectionValue]);

  return (
    <Tabs
      value={activeSection}
      onTabChange={setSection}
      variant="pills"
      sx={(theme) => ({
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        "& [role='tab'][data-active]": {
          backgroundColor: `${
            theme.colorScheme === "dark"
              ? theme.colors.dark[5]
              : theme.colors.cyan[0]
          } !important`,
          color: `${
            theme.colorScheme === "dark" ? theme.white : theme.colors.cyan[9]
          } !important`,
          fontWeight: 600,
        },
      })}
    >
      <Tabs.List>
        <Tabs.Tab value="setup">{i18n.t("Setup")}</Tabs.Tab>
        <Tabs.Tab value="recipients">{i18n.t("Recipients")}</Tabs.Tab>
        {isEmail && <Tabs.Tab value="email">{i18n.t("Email")}</Tabs.Tab>}
      </Tabs.List>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", paddingTop: 16 }}>
        <Tabs.Panel value="setup">
          <Stack spacing="md">
            <FieldCard title="Basic">
              <SimpleGrid cols={2} spacing="md" breakpoints={[{ maxWidth: "sm", cols: 1 }]}>
                <TextInput
                  label="Name"
                  required
                  size="sm"
                  placeholder="Notify maker on complete"
                  value={notification.name ?? ""}
                  readOnly={readOnly}
                  onChange={(e) => patch({ name: e.target.value })}
                />
                <Select
                  label="Channel"
                  size="sm"
                  data={channelOptions}
                  value={notification.channel ?? "EMAIL"}
                  readOnly={readOnly}
                  onChange={(v) => patch({ channel: v ?? "EMAIL" })}
                />
              </SimpleGrid>
            </FieldCard>
            <FieldCard title="When to send">
              <Select
                aria-label="When to send"
                size="sm"
                placeholder="Select when to send"
                description={
                  sendTimingFromNotification(notification)
                    .split(",")
                    .includes("APPROVED")
                    ? APPROVED_SEND_TIMING_NOTE
                    : undefined
                }
                inputWrapperOrder={["input", "description", "error"]}
                data={sendTimingSelectData(notification)}
                value={sendTimingFromNotification(notification) || null}
                readOnly={readOnly}
                withinPortal
                onChange={handleSendTimingChange}
              />
            </FieldCard>
            <FieldCard title="Send conditions">
              <McaNotificationConditionsEditor
                conditions={notification.conditions ?? []}
                conditionLogic={notification.conditionLogic ?? "OR"}
                entityKey={targetEntity}
                stepFields={stepFields}
                onChange={(next) => patch(next)}
              />
            </FieldCard>
            {!isEmail && smsEnabled && (
              <FieldCard title="SMS">
                <McaNotificationTemplateInput
                  label="SMS message"
                  required
                  multiline
                  minRows={4}
                  placeholder="Hello {{requestCode}}"
                  value={notification.message ?? ""}
                  readOnly={readOnly}
                  onChange={(message) => patch({ message })}
                  variables={templateVariables}
                />
              </FieldCard>
            )}
            {!isEmail && !smsEnabled && (
              <Text size="sm" color="dimmed">
                SMS delivery is not configured yet. This rule will be saved but not sent.
              </Text>
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="recipients">
          <Stack spacing="md">
            <FieldCard title="Notify">
              <Stack spacing="sm">
                <Select
                  label="Notify to"
                  size="sm"
                  data={MCA_NOTIFY_BY}
                  value={recipientMode}
                  readOnly={readOnly}
                  withinPortal
                  onChange={handleNotifyByChange}
                />
                {recipientMode === "ENTITY_ROLE" && (
                  <>
                    <Select
                      label="Entity role"
                      size="sm"
                      placeholder="Select role"
                      data={entityRoleOptions(targetEntity)}
                      value={entityRole || null}
                      readOnly={readOnly}
                      required
                      withinPortal
                      onChange={handleEntityRoleChange}
                    />
                    {!targetEntity && (
                      <Text size="xs" color="orange">
                        Select a target entity in Trigger Config first. Entity roles depend on that entity.
                      </Text>
                    )}
                  </>
                )}
                {recipientMode === "ENTITY_ROLE" && entityRole === "insurer" && (
                  <Select
                    label="Insurer email purpose"
                    size="sm"
                    placeholder="Select purpose"
                    description="Sends to every insurer email marked with this purpose"
                    data={insurerPurposeOptions}
                    value={notification.notifyEntityProviderPurpose || null}
                    readOnly={readOnly}
                    required
                    withinPortal
                    onChange={(value) =>
                      patch({ notifyEntityProviderPurpose: value || "" })
                    }
                  />
                )}
                {recipientMode === "EMAILS" && (
                  <MultiSelect
                    label="Email addresses"
                    size="sm"
                    placeholder="Type email and press Enter"
                    data={emailOptions}
                    searchable
                    creatable
                    value={notification.notifyEmails ?? []}
                    readOnly={readOnly}
                    getCreateLabel={(query) => `+ Add ${query}`}
                    onCreate={(query) => {
                      const trimmed = query.trim();
                      if (!trimmed) return null;
                      setEmailOptions((current) =>
                        current.includes(trimmed) ? current : [...current, trimmed]
                      );
                      return trimmed;
                    }}
                    onChange={(notifyEmails) => patch({ notifyEmails })}
                  />
                )}
                {recipientMode === "USERS" && (
                  <RemoteSelect2
                    form={{
                      values: notification,
                      setFieldValue: (field, val) => patch({ [field]: val }),
                      markUserEdited: () => {},
                    }}
                    name="notifyUsers"
                    label="Notify users"
                    apiEntity="user"
                    labelRender={formatStaffLabel}
                    multiple
                    size="sm"
                    readOnly={readOnly}
                    placeholder="Search users"
                    searchFields={["name", "staffId", "cname", "email"]}
                  />
                )}
                {recipientMode === "USER_ROLE" && (
                  <RemoteSelect2
                    form={{
                      values: notification,
                      setFieldValue: (field, val) => patch({ [field]: val }),
                      markUserEdited: () => {},
                    }}
                    name="notifyRoles"
                    label="Notify roles"
                    apiEntity="userRole"
                    labelField="name"
                    multiple
                    size="sm"
                    readOnly={readOnly}
                    placeholder="Search roles"
                    searchFields={["name"]}
                  />
                )}
              </Stack>
            </FieldCard>
          </Stack>
        </Tabs.Panel>

        {isEmail && (
          <Tabs.Panel value="email">
            <EntityDocumentEmailFields
              grouped
              between={
                <FieldCard title="Message">
                  <Stack spacing="sm">
                    <McaNotificationTemplateInput
                      label="Email subject"
                      required
                      placeholder="Step completed: {{requestCode}}"
                      value={notification.subject ?? ""}
                      readOnly={readOnly}
                      onChange={(subject) => patch({ subject })}
                      variables={templateVariables}
                    />
                    <McaNotificationTemplateInput
                      label="Email message"
                      required
                      multiline
                      minRows={8}
                      placeholder="Request {{requestCode}} — step {{stepName}}"
                      value={notification.message ?? ""}
                      readOnly={readOnly}
                      onChange={(message) => patch({ message })}
                      variables={templateVariables}
                    />
                  </Stack>
                </FieldCard>
              }
              targetEntity={targetEntity}
              templateType="MCA"
              passwordTemplateType="MCA_PASSWORD"
              subject={notification.subject ?? ""}
              body={notification.message ?? ""}
              cc={notification.cc ?? ""}
              bcc={notification.bcc ?? ""}
              attachmentKeys={notification.attachmentKeys ?? []}
              encrypt={!!notification.encrypt}
              passwordSubject={notification.passwordSubject ?? ""}
              passwordBody={notification.passwordBody ?? ""}
              readOnly={readOnly}
              onChange={(partial) => {
                const next = { ...partial };
                if (Object.prototype.hasOwnProperty.call(partial, "body")) {
                  next.message = partial.body;
                  delete next.body;
                }
                patch(next);
              }}
            />
          </Tabs.Panel>
        )}
      </Box>
    </Tabs>
  );
};

export default McaStepNotificationForm;
